import {
  DateCell,
  HoverableLabel,
  LocationCell,
  NavigateButton,
} from 'components/common';
import { RowLayout } from 'components/core/ListTable/types';
import { scopedT } from 'utils/functions/Translations';

import { Pill, reactIcons as Icons } from '@project44/manifest-ds';
import { IOrder } from '@project44-models/shipment-list';

import {
  ORDER_DELIVERY_OPTIONS,
  ORDER_READY_OPTIONS,
  ORDER_SUBMISSION_OPTIONS,
} from './MasterSort';
import { PATHS } from './Routes';

export const IDENTIFIER_TITLE_MAP: Record<string, string> = {
  PURCHASE_ORDER: 'Purchase Order',
  SALES_ORDER: 'Sales Order',
  ADVANCED_SHIPMENT_NOTICE: 'Advanced Shipment Notice',
  WAREHOUSE_MOVEMENT_ORDER: 'Warehouse Movement Order',
};

export const IDENTIFIER_TAG_MAP: Record<string, string> = {
  PURCHASE_ORDER: 'PO',
  SALES_ORDER: 'SO',
  ADVANCED_SHIPMENT_NOTICE: 'ASN',
  WAREHOUSE_MOVEMENT_ORDER: 'WMO',
};

export const STATUS_TITLE_MAP: Record<string, string> = {
  SUBMITTED: 'Submitted',
  RECEIVED: 'Received',
  PROCESSING: 'Processing',
  ACCEPTED: 'Accepted',
  FULFILLED: 'Fulfilled',
  REJECTED: 'Rejected',
  CANCELED: 'Canceled',
};

export const STATUS_ICON_MAP: Record<string, JSX.Element> = {
  SUBMITTED: <Icons.ClipboardWithPlus data-testid="clipboard-plus-icon" />,
  RECEIVED: <Icons.ClipboardWithArrow data-testid="clipboard-arrow-icon" />,
  PROCESSING: <Icons.Reload data-testid="reload-icon" />,
  ACCEPTED: <Icons.ClipboardWithCheck data-testid="clipboard-check-icon" />,
  FULFILLED: <Icons.CheckDouble data-testid="check-double-icon" />,
  REJECTED: <Icons.Reset data-testid="reset-icon" />,
  CANCELED: <Icons.Reset data-testid="reset-icon" />,
};

export const rowLayout: RowLayout<IOrder> = [
  {
    id: 'order-number',
    element: ({ orderIdentifier = {} }) => {
      const { value = '', identifierType = '' } = orderIdentifier;
      return (
        <HoverableLabel
          label={IDENTIFIER_TAG_MAP[identifierType]}
          labelTooltip={IDENTIFIER_TITLE_MAP[value]}
          text={value || ''}
        />
      );
    },
  },
  {
    id: 'order-status',
    element: ({ currentStatus }) => (
      <Pill
        icon={STATUS_ICON_MAP[currentStatus || 'PROCESSING']}
        label={STATUS_TITLE_MAP[currentStatus || 'PROCESSING']}
      />
    ),
  },
  {
    id: 'origin',
    element: ({ origin }) => (
      <LocationCell
        locationName={origin?.name || '-'}
        cityState={origin?.address.cityState || ''}
      />
    ),
  },
  {
    id: 'destination',
    element: ({ destination }) => (
      <LocationCell
        locationName={destination?.name || '-'}
        cityState={destination?.address.cityState || ''}
      />
    ),
  },
  { id: 'supplier', element: ({ supplierName }) => supplierName || '-' },
  {
    id: 'supplier-ready-date',
    element: ({ supplierReadyDate }) => (
      <DateCell dateTime={supplierReadyDate?.startDateTime} />
    ),
  },
  {
    id: 'order-due-date',
    element: ({ orderDueDate }) => (
      <DateCell dateTime={orderDueDate?.startDateTime} />
    ),
  },
  {
    id: 'order-submission-date',
    element: ({ orderSubmissionDate }) => (
      <DateCell dateTime={orderSubmissionDate} />
    ),
  },
  {
    id: 'related-shipments',
    element: ({ orderId, shipmentIds = [] }) => {
      const idCount = shipmentIds?.length;
      const label = Math.abs(idCount) === 1 ? 'Shipment' : 'Shipments';
      return (
        <NavigateButton
          isDisabled={!idCount}
          text={idCount ? `${idCount} ${label}` : 'No Shipments'}
          to={{
            pathname: `/results/${PATHS.relatedView}`,
            search: `?relatedView=order:${orderId}`,
          }}
        />
      );
    },
  },
];

const t = scopedT();

export const ORDER_TABLE_HEADERS = [
  { id: 'order-number', label: t('core.orderNumber'), width: '220px' },
  { id: 'order-status', label: t('core.orderStatus'), width: '140px' },
  { id: 'origin', label: t('core.origin'), width: '220px' },
  { id: 'destination', label: t('core.destination'), width: '220px' },
  { id: 'supplier', label: t('core.supplier'), width: '180px' },
  {
    id: 'supplier-ready-date',
    label: t('core.supplierReadyDate'),
    width: '220px',
    options: ORDER_READY_OPTIONS,
  },
  {
    id: 'order-due-date',
    label: t('core.orderDueDate'),
    width: '190px',
    options: ORDER_DELIVERY_OPTIONS,
  },
  {
    id: 'order-submission-date',
    label: t('core.orderSubmissionDate'),
    width: '230px',
    options: ORDER_SUBMISSION_OPTIONS,
  },
  {
    id: 'related-shipments',
    label: t('core.relatedShipments'),
    width: '160px',
  },
];
